/**
 * WaCoverageCheckCard — verificador automático de cobertura + costo
 * para mensajes de ubicación. Auto-fetch al render (sin click); cachea
 * por (conv, lat, lng) para sobrevivir la virtualización de react-window.
 */

import { lazy, Suspense, useEffect, useState } from 'react';
import {
  Box,
  BlockStack,
  InlineStack,
  Text,
  Spinner,
  Button,
  ButtonGroup,
  SkeletonBodyText,
  Banner,
} from '@shopify/polaris';
import { supabase } from '@/lib/supabase';
/**
 * 🪤 `lazy()` y no import estático: este modal usa `react-leaflet` (~440 KB) y esta tarjeta cuelga
 * del layout del panel, así que en estático la librería de mapas entra al ARRANQUE de todo el panel
 * por un modal que sólo se abre al tocar «ver en el mapa». Ver `npm run check:arranque`.
 */
const WaCoverageMapModal = lazy(() =>
  import('./WaCoverageMapModal').then((m) => ({ default: m.WaCoverageMapModal })),
);
import { prefetchDeliveryZones } from './waDeliveryZonesCache';
import {
  buildInZoneQuote,
  buildOutOfZoneQuote,
  formatPEN,
  parseLatLng,
} from './waUbicacion';

interface Courier {
  id: string;
  name: string;
  slug: string | null;
  type: string;
  fee: number | null;
  color: string | null;
  eta_minutes: number | null;
}

interface CoverageResult {
  covered: boolean;
  has_setup: boolean;
  zone: { id: string; name: string; district_name: string | null; fee: number } | null;
  couriers: Courier[];
  reason?: string;
}

interface Props {
  body: string;
  conversationId: string;
  onSendQuickReply?: (text: string) => void;
}

const COVERAGE_CACHE = new Map<string, CoverageResult>();

export function WaCoverageCheckCard({ body, conversationId, onSendQuickReply }: Props) {
  const cacheKey = `${conversationId}|${body}`;
  const cached = COVERAGE_CACHE.get(cacheKey) ?? null;
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CoverageResult | null>(cached);
  const [mapOpen, setMapOpen] = useState(false);

  // Prefetch zonas de delivery en background → cuando el operador
  // abra "Ver mapa", el modal lee de cache (≈0ms) en lugar de
  // fetchear 56 polígonos en cold path.
  useEffect(() => {
    if (conversationId && result?.has_setup) {
      prefetchDeliveryZones(conversationId);
    }
  }, [conversationId, result?.has_setup]);

  useEffect(() => {
    if (COVERAGE_CACHE.has(cacheKey)) {
      setResult(COVERAGE_CACHE.get(cacheKey) ?? null);
      setLoading(false);
      return;
    }
    const coords = parseLatLng(body);
    if (!coords || !conversationId) {
      setLoading(false);
      return;
    }
    const [lat, lng] = coords;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      const { data, error: invokeErr } = await supabase.functions.invoke('whatsapp-send-message', {
        body: { action: 'check_coverage', conversation_id: conversationId, lat, lng },
      });
      if (cancelled) return;
      if (invokeErr) {
        setError(invokeErr.message ?? 'Error al verificar cobertura');
        setLoading(false);
        return;
      }
      const typed = data as CoverageResult;
      COVERAGE_CACHE.set(cacheKey, typed);
      setResult(typed);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [body, conversationId, cacheKey]);

  const handleQuote = () => {
    if (!onSendQuickReply || !result) return;
    if (result.covered && result.zone) {
      const courier = result.couriers[0];
      onSendQuickReply(buildInZoneQuote({
        districtName: result.zone.district_name,
        fee: result.zone.fee,
        courierName: courier?.name,
        courierEtaMinutes: courier?.eta_minutes,
      }));
    } else {
      onSendQuickReply(buildOutOfZoneQuote());
    }
  };

  if (loading) {
    return (
      <Box padding="300" background="bg-surface-secondary" borderRadius="200">
        <BlockStack gap="300">
          <InlineStack gap="200" blockAlign="center">
            <Spinner size="small" />
            <Text as="span" variant="bodySm" tone="subdued">Verificando cobertura…</Text>
          </InlineStack>
          <SkeletonBodyText lines={2} />
        </BlockStack>
      </Box>
    );
  }

  if (error || !result) {
    return (
      <Box padding="300" background="bg-surface-secondary" borderRadius="200">
        <Text as="p" variant="bodySm" tone="subdued">
          No se pudo verificar la cobertura{error ? `: ${error}` : ''}
        </Text>
      </Box>
    );
  }

  const subtitle = result.covered
    ? result.zone
      ? `${result.zone.district_name ?? result.zone.name} · Zona "${result.zone.name}"`
      : 'Cobertura confirmada'
    : result.has_setup
      ? 'No llegamos a esta zona'
      : 'Sin zonas configuradas';

  const firstCourier = result.couriers[0];

  return (
    <Box padding="300" background="bg-surface-secondary" borderRadius="200">
      <BlockStack gap="300">
        <BlockStack gap="050">
          <InlineStack gap="200" blockAlign="center" wrap={false}>
            <Text as="span" variant="headingMd">🛵</Text>
            <Text as="h4" variant="headingSm" fontWeight="semibold">
              Cobertura de envío
            </Text>
          </InlineStack>
          <Text as="p" variant="bodySm" tone="subdued">{subtitle}</Text>
        </BlockStack>

        {result.covered && result.zone ? (
          <Box padding="300" background="bg-surface" borderRadius="200" borderWidth="025" borderColor="border">
            <InlineStack align="space-between" blockAlign="center" wrap={false}>
              <BlockStack gap="050">
                <Text as="p" variant="bodySm" tone="subdued">Costo de envío</Text>
                <Text as="p" variant="headingLg" tone="success">
                  {formatPEN(result.zone.fee)}
                </Text>
              </BlockStack>
              {firstCourier ? (
                <BlockStack gap="050" inlineAlign="end">
                  <Text as="p" variant="bodySm" tone="subdued">Courier</Text>
                  <InlineStack gap="100" blockAlign="center">
                    <span style={{
                      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                      background: firstCourier.color ?? '#9e9e9e',
                    }} />
                    <Text as="span" variant="bodyMd" fontWeight="medium">
                      {firstCourier.name}
                    </Text>
                  </InlineStack>
                </BlockStack>
              ) : null}
            </InlineStack>
          </Box>
        ) : result.has_setup ? (
          <Banner
            tone="warning"
            action={{ content: 'Ajustar configuración', url: '/chats/delivery-zones', external: true }}
          >
            <Text as="p" variant="bodySm">
              Esta ubicación está fuera de tus zonas activas.
            </Text>
          </Banner>
        ) : (
          <Banner
            tone="info"
            action={{ content: 'Empezar', url: '/chats/delivery-zones', external: true }}
          >
            <Text as="p" variant="bodySm">
              Configura zonas de delivery para que tus agentes puedan cotizar
              automáticamente desde el chat.
            </Text>
          </Banner>
        )}

        <ButtonGroup fullWidth>
          {result.has_setup ? (
            <Button onClick={() => setMapOpen(true)} size="slim">
              Ver mapa
            </Button>
          ) : null}
          {onSendQuickReply ? (
            <Button onClick={handleQuote} variant="primary" size="slim">
              {result.covered ? 'Cotizar al cliente' : 'Avisar al cliente'}
            </Button>
          ) : null}
        </ButtonGroup>
      </BlockStack>

      {/* 🪤 Sólo se monta cuando se abre. Sin este guardia, `lazy()` bajaría el chunk igual al
          renderizar la tarjeta y no se ahorraría nada en la práctica. */}
      {mapOpen ? (
        <Suspense fallback={null}>
          <WaCoverageMapModal
            open={mapOpen}
            onClose={() => setMapOpen(false)}
            body={body}
            conversationId={conversationId}
            onSendQuickReply={onSendQuickReply}
          />
        </Suspense>
      ) : null}
    </Box>
  );
}
